import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const DEMO_ACCOUNTS = [
  { email: 'shiz.asif@altveno.com', password: 'Test1234!', label: 'Altveno Admin',   role: 'admin',            color: '#C9A84C' },
  { email: 'manager@altveno.com',   password: 'Test1234!', label: 'Manager',          role: 'manager',          color: '#5dc9a8' },
  { email: 'allocator@test.com',    password: 'Test1234!', label: 'Allocator',        role: 'allocator',        color: '#0D6E8A' },
  { email: 'provider@altveno.com',  password: 'Test1234!', label: 'Service Provider', role: 'service_provider', color: '#8B5CF6' },
  { email: 'candidate@altveno.com', password: 'Test1234!', label: 'Candidate',        role: 'candidate',        color: '#F59E0B' },
];

const NAVY   = '#0A1628';
const GOLD    = '#C9A84C';

export default function DemoSwitcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState<string | null>(null);
  const [errorEmail, setErrorEmail] = useState<string | null>(null);
  const [hoverEmail, setHoverEmail] = useState<string | null>(null);

  const currentEmail = user?.email?.toLowerCase() ?? '';
  const isDemoUser = DEMO_ACCOUNTS.some(a => a.email.toLowerCase() === currentEmail);

  // Only surface the switcher for the recognised demo accounts.
  if (!isDemoUser) return null;

  const handleSwitch = async (email: string, password: string) => {
    setErrorEmail(null);
    setLoadingEmail(email);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setErrorEmail(email);
        setLoadingEmail(null);
        return;
      }
      // /dashboard re-routes to the correct home based on the new role.
      setOpen(false);
      setLoadingEmail(null);
      navigate('/dashboard');
    } catch {
      setErrorEmail(email);
      setLoadingEmail(null);
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Expanded card ── */}
      {open && (
        <div style={{
          position: 'absolute', bottom: 44, right: 0,
          width: 268,
          background: NAVY,
          border: `1px solid ${GOLD}`,
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          padding: 14,
          marginBottom: 8,
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 13, color: '#FFFFFF', fontWeight: 400,
            }}>
              Demo Accounts
            </span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.55)', fontSize: 16, lineHeight: 1,
                padding: 2, transition: 'all 0.15s ease',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = GOLD; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.55)'; }}
            >
              ✕
            </button>
          </div>

          {/* Account list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {DEMO_ACCOUNTS.map(acc => {
              const isActive = acc.email.toLowerCase() === currentEmail;
              const isLoading = loadingEmail === acc.email;
              const isHover = hoverEmail === acc.email;
              return (
                <div key={acc.email}>
                  <button
                    onClick={() => !isActive && !isLoading && handleSwitch(acc.email, acc.password)}
                    onMouseEnter={() => setHoverEmail(acc.email)}
                    onMouseLeave={() => setHoverEmail(null)}
                    disabled={isActive || isLoading}
                    style={{
                      width: '100%',
                      display: 'flex', alignItems: 'center', gap: 10,
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: 8,
                      cursor: isActive ? 'default' : 'pointer',
                      background: isActive
                        ? 'rgba(201,168,76,0.10)'
                        : isHover ? 'rgba(255,255,255,0.06)' : 'transparent',
                      border: isActive ? `1px solid ${GOLD}` : '1px solid transparent',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {/* Role dot */}
                    <span style={{
                      width: 9, height: 9, borderRadius: '50%',
                      background: acc.color, flexShrink: 0,
                      boxShadow: `0 0 0 2px ${acc.color}33`,
                    }} />

                    {/* Label + email */}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 12.5, fontWeight: 600,
                        color: '#FFFFFF',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {acc.label}
                      </span>
                      <span style={{
                        display: 'block', fontSize: 10.5,
                        color: 'rgba(255,255,255,0.45)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {acc.email}
                      </span>
                    </span>

                    {/* Right adornment: spinner / checkmark */}
                    {isLoading ? (
                      <span style={{
                        width: 14, height: 14, flexShrink: 0,
                        border: '2px solid rgba(255,255,255,0.25)',
                        borderTopColor: GOLD, borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'altveno-demo-spin 0.6s linear infinite',
                      }} />
                    ) : isActive ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                        stroke={GOLD} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
                        style={{ flexShrink: 0 }}>
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    ) : null}
                  </button>

                  {/* Error */}
                  {errorEmail === acc.email && (
                    <div style={{
                      fontSize: 10.5, color: '#F87171',
                      padding: '3px 10px 0',
                    }}>
                      Couldn’t switch, check credentials and try again.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Collapsed pill ── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: NAVY,
          border: `1px solid ${GOLD}`,
          borderRadius: 20,
          padding: '6px 14px',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          transition: 'all 0.15s ease',
        }}
      >
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: GOLD, boxShadow: `0 0 6px ${GOLD}`,
        }} />
        <span style={{
          fontSize: 10, fontWeight: 700, color: GOLD,
          letterSpacing: 1.5,
        }}>
          DEMO
        </span>
      </button>

      <style>{`@keyframes altveno-demo-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
